// Kaltiq private-chat payload encryption with authorized key recovery.
// This is explicitly not zero-knowledge: the one-time enrollment copy of each
// identity key is wrapped by the server escrow KEK. Message plaintext and
// content keys never enter the HTTP API.

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const DB_NAME = 'kaltiq-e2ee';
const DB_VERSION = 1;
const STORE = 'identities';
const ALGORITHM = 'ECDH-P256+HKDF-SHA256+AES-256-GCM';
const MIME = 'application/x-kaltiq-e2ee+json';
const identityCache = new Map();

function bytesToBase64(value) {
  let out = '';
  const bytes = new Uint8Array(value);
  for (let i = 0; i < bytes.length; i += 0x8000) out += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(out);
}

function base64ToBytes(value) {
  const raw = atob(value);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function openIdentityDB() {
  if (!globalThis.indexedDB) return Promise.reject(new Error('Secure device storage is unavailable'));
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error('Unable to open secure device storage'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, {keyPath: 'id'});
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function readIdentity(id) {
  const db = await openIdentityDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(id);
    req.onerror = () => reject(req.error || new Error('Unable to read device identity'));
    req.onsuccess = () => resolve(req.result || null);
    tx.oncomplete = () => db.close();
  });
}

async function writeIdentity(record) {
  const db = await openIdentityDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(record);
    tx.onerror = () => reject(tx.error || new Error('Unable to save device identity'));
    tx.oncomplete = () => { db.close(); resolve(record); };
  });
}

async function deriveWrapKey(privateKey, publicKey, salt, context) {
  const shared = await crypto.subtle.deriveBits({name: 'ECDH', public: publicKey}, privateKey, 256);
  const material = await crypto.subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey({name: 'HKDF', hash: 'SHA-256', salt, info: encoder.encode(context)},
    material, {name: 'AES-GCM', length: 256}, false, ['encrypt', 'decrypt']);
}

export async function ensureEscrowedIdentity(api, uid, deviceId) {
  if (!globalThis.crypto || !crypto.subtle) throw new Error('WebCrypto is unavailable');
  const id = `${uid}:${deviceId}`;
  if (identityCache.has(id)) return identityCache.get(id);
  const existing = await readIdentity(id);
  if (existing && existing.privateKey && existing.publicKey) {
    identityCache.set(id, existing);
    return existing;
  }
  const pair = await crypto.subtle.generateKey({name: 'ECDH', namedCurve: 'P-256'}, true, ['deriveBits']);
  const publicKeyBytes = await crypto.subtle.exportKey('spki', pair.publicKey);
  const privateKeyBytes = await crypto.subtle.exportKey('pkcs8', pair.privateKey);
  let receipt;
  try {
    receipt = await api.escrowIdentityKey(deviceId, ALGORITHM,
      bytesToBase64(publicKeyBytes), bytesToBase64(privateKeyBytes));
  } finally {
    new Uint8Array(privateKeyBytes).fill(0);
  }
  // Store a non-exportable copy locally after enrollment.
  const exportedForImport = await crypto.subtle.exportKey('pkcs8', pair.privateKey);
  const privateKey = await crypto.subtle.importKey('pkcs8', exportedForImport,
    {name: 'ECDH', namedCurve: 'P-256'}, false, ['deriveBits']);
  new Uint8Array(exportedForImport).fill(0);
  const record = {id, uid, deviceId, publicKey: pair.publicKey, privateKey,
    publicKeyB64: bytesToBase64(publicKeyBytes), escrowId: receipt.id, createdAt: new Date().toISOString()};
  await writeIdentity(record);
  identityCache.set(id, record);
  return record;
}

export async function encryptPrivatePayload(api, senderUid, recipientUid, deviceId, topic, payload) {
  const identity = await ensureEscrowedIdentity(api, senderUid, deviceId);
  const directories = await Promise.all([api.keyDirectory(senderUid), api.keyDirectory(recipientUid)]);
  const keys = [];
  const seen = new Set();
  directories.forEach(directory => (directory.keys || []).forEach(key => {
    const keyId = String(key.id || `${directory.user}:${key.device_id}`);
    if (!seen.has(keyId)) { seen.add(keyId); keys.push(Object.assign({uid: directory.user}, key)); }
  }));
  if (!(directories[1].keys || []).length) throw new Error('The recipient has no enrolled encrypted device yet');
  if (keys.length > 64) throw new Error('Too many active encrypted devices');

  const context = `kaltiq-private-chat:v2:${topic}`;
  const contentKey = await crypto.subtle.generateKey({name: 'AES-GCM', length: 256}, true, ['encrypt', 'decrypt']);
  const contentKeyRaw = await crypto.subtle.exportKey('raw', contentKey);
  const contentIV = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({name: 'AES-GCM', iv: contentIV, additionalData: encoder.encode(context)},
    contentKey, encoder.encode(JSON.stringify(payload)));
  const ephemeral = await crypto.subtle.generateKey({name: 'ECDH', namedCurve: 'P-256'}, true, ['deriveBits']);
  const ephemeralPublic = bytesToBase64(await crypto.subtle.exportKey('spki', ephemeral.publicKey));
  const recipients = [];
  try {
    for (const keyInfo of keys) {
      const publicKey = await crypto.subtle.importKey('spki', base64ToBytes(keyInfo.public_key),
        {name: 'ECDH', namedCurve: 'P-256'}, false, []);
      const salt = crypto.getRandomValues(new Uint8Array(32));
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const keyContext = `${context}:${keyInfo.id}`;
      const wrapKey = await deriveWrapKey(ephemeral.privateKey, publicKey, salt, keyContext);
      const wrappedKey = await crypto.subtle.encrypt({name: 'AES-GCM', iv, additionalData: encoder.encode(keyContext)}, wrapKey, contentKeyRaw);
      recipients.push({key_id: keyInfo.id, uid: keyInfo.uid, device_id: keyInfo.device_id,
        salt: bytesToBase64(salt), iv: bytesToBase64(iv), wrapped_key: bytesToBase64(wrappedKey)});
    }
  } finally {
    new Uint8Array(contentKeyRaw).fill(0);
  }
  return {v: 2, alg: ALGORITHM, topic, sender: senderUid, sender_device: identity.deviceId,
    epk: ephemeralPublic, iv: bytesToBase64(contentIV), ciphertext: bytesToBase64(ciphertext), recipients};
}

export async function decryptPrivatePayload(api, uid, deviceId, envelope) {
  if (!envelope || envelope.v !== 2 || envelope.alg !== ALGORITHM) throw new Error('Unsupported encrypted message');
  const identity = await ensureEscrowedIdentity(api, uid, deviceId);
  const recipient = (envelope.recipients || []).find(item => item.device_id === deviceId && String(item.uid) === String(uid));
  if (!recipient) throw new Error('This message was not encrypted to this device');
  const context = `kaltiq-private-chat:v2:${envelope.topic}`;
  const keyContext = `${context}:${recipient.key_id}`;
  const ephemeral = await crypto.subtle.importKey('spki', base64ToBytes(envelope.epk),
    {name: 'ECDH', namedCurve: 'P-256'}, false, []);
  const wrapKey = await deriveWrapKey(identity.privateKey, ephemeral, base64ToBytes(recipient.salt), keyContext);
  const raw = await crypto.subtle.decrypt({name: 'AES-GCM', iv: base64ToBytes(recipient.iv), additionalData: encoder.encode(keyContext)},
    wrapKey, base64ToBytes(recipient.wrapped_key));
  try {
    const contentKey = await crypto.subtle.importKey('raw', raw, {name: 'AES-GCM'}, false, ['decrypt']);
    const plaintext = await crypto.subtle.decrypt({name: 'AES-GCM', iv: base64ToBytes(envelope.iv), additionalData: encoder.encode(context)},
      contentKey, base64ToBytes(envelope.ciphertext));
    return JSON.parse(decoder.decode(plaintext));
  } finally {
    new Uint8Array(raw).fill(0);
  }
}

export function isEncryptedPrivatePayload(head, content) {
  return !!(head && head.e2ee && head.mime === MIME && content && content.v === 2);
}

export {ALGORITHM, MIME, bytesToBase64, base64ToBytes};
